import { t } from "@lingui/macro";
import { FacebookLogo, InstagramLogo, LinkedinLogo, TwitterLogo } from "@phosphor-icons/react";
import { Separator } from "@stylingcv/ui";
import { Link } from "react-router-dom";

import { Copyright } from "@/client/components/copyright";
import { LocaleSwitch } from "@/client/components/locale-switch";
import { Logo } from "@/client/components/logo";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { useLocalizedNavigation } from "@/client/hooks/useLocalizedNavigation";

export const Footer = () => {
  const { getLocalizedHref } = useLocalizedNavigation();

  const footerLinks = {
    product: [
      { name: t`Resume Builder`, href: "/builder" },
      { name: t`Smart Generator`, href: "/ai-generator" },
      { name: t`CV Templates`, href: "/templates" },
      { name: t`Resume Examples`, href: "/examples" },
      { name: t`Features`, href: "/features" },
      { name: t`Pricing`, href: "/pricing" },
    ],
    aiTools: [
      { name: t`Stylion Challenge: Resume Checker`, href: "/stylion" },
      { name: t`ATS Checker`, href: "/ats-score-checker" },
      { name: t`Summary regenerating`, href: "/summary-regenerating" },
    ],
    company: [
      { name: t`About Us`, href: "/about" },
      { name: t`Our Manifesto`, href: "/manifesto" },
      { name: t`Vision 2030 (KSA)`, href: "/saudi-arabia" },
      { name: t`FAQ`, href: "/faq" },
    ],
    guides: [
      {
        name: t`Jadarat Resume Guide`,
        href: "https://stylion.io/ar/سيرة-ذاتية-جدارات/",
        external: true,
      },
      {
        name: t`Saudi ATS Guide`,
        href: "https://stylion.io/ar/ATS-السعودية/",
        external: true,
      },
      {
        name: t`Best Arabic Resume Builders`,
        href: "https://stylion.io/ar/أفضل-10-مواقع-إنشاء-سيرة-ذاتية-مجانية-بالعربي/",
        external: true,
      },
    ],
    support: [
      { name: t`Help Center`, href: "/help" },
      { name: t`Contact Us`, href: "/contact" },
      { name: t`Login`, href: "/auth/login" },
      { name: t`Register`, href: "/auth/register" },
      { name: t`Privacy Policy`, href: "https://stylion.io/privacy-policy", external: true },
      { name: t`Terms of Use`, href: "https://stylion.io/terms-of-use", external: true },
    ],
  };

  return (
    <footer className="relative border-t bg-background/50 backdrop-blur-sm">
      {/* Decorative top gradient */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

      <div className="container px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-5 xl:gap-16">
          {/* Column 1: Brand + About + Social */}
          <div className="space-y-6">
            <Logo size={96} className="-ml-3" />
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t`Stylion is your AI-powered career companion. Create professional resumes, cover letters, and optimized profiles in minutes.`}
            </p>

            {/* Social Icons */}
            <div className="flex items-center gap-3">
              <a
                href="#"
                className="rounded-full bg-secondary/50 p-2 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
              >
                <TwitterLogo size={18} weight="fill" />
              </a>
              <a
                href="#"
                className="rounded-full bg-secondary/50 p-2 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
              >
                <FacebookLogo size={18} weight="fill" />
              </a>
              <a
                href="#"
                className="rounded-full bg-secondary/50 p-2 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
              >
                <LinkedinLogo size={18} weight="fill" />
              </a>
              <a
                href="#"
                className="rounded-full bg-secondary/50 p-2 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
              >
                <InstagramLogo size={18} weight="fill" />
              </a>
            </div>
          </div>

          {/* Column 2: Product + AI tools */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              {t`Product`}
            </h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <Link
                    to={getLocalizedHref(link.href)}
                    className="text-xs text-muted-foreground transition-colors hover:text-primary"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="pt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t`AI tools`}
            </h4>
            <ul className="space-y-3">
              {footerLinks.aiTools.map((link) => (
                <li key={link.name}>
                  <Link
                    to={getLocalizedHref(link.href)}
                    className="text-xs text-muted-foreground transition-colors hover:text-primary"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Company */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              {t`Company`}
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={getLocalizedHref(link.href)}
                    className="text-xs text-muted-foreground transition-colors hover:text-primary"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="pt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t`Guides`}
            </h4>
            <ul className="space-y-3">
              {footerLinks.guides.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground transition-colors hover:text-primary"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Support */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              {t`Support`}
            </h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-muted-foreground transition-colors hover:text-primary"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={getLocalizedHref(link.href)}
                      className="text-xs text-muted-foreground transition-colors hover:text-primary"
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5: Mobile QR + Classic Builder */}
          <div className="space-y-4">
            {/* Mobile QR Code */}
            <div className="rounded-xl border bg-card p-4 shadow-sm dark:bg-card/50">
              <p className="mb-3 text-xs font-medium text-muted-foreground">
                {t`Scan to build on mobile`}
              </p>
              <div className="flex justify-center">
                <div className="group relative overflow-hidden rounded-lg bg-white p-2 shadow-sm">
                  <img
                    src="/images/frame.svg"
                    alt={t`QR Code`}
                    className="h-24 w-24 object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Classic Builder v1 - Prominent */}
            <a
              href="https://app.stylion.io"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl bg-gradient-to-r from-slate-100 to-slate-200 p-4 text-center text-sm font-medium text-slate-700 shadow-sm transition-all hover:from-slate-200 hover:to-slate-300 hover:shadow-md dark:from-slate-800 dark:to-slate-700 dark:text-slate-200 dark:hover:from-slate-700 dark:hover:to-slate-600"
            >
              {t`Try our classic builder v1`}
            </a>
          </div>
        </div>

        <Separator className="my-12" />

        <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
          <Copyright className="text-sm text-muted-foreground" />

          <div className="flex items-center gap-4 rounded-full border bg-background/50 px-4 py-2 backdrop-blur-sm">
            <LocaleSwitch className="!size-6 !p-0 !border-none !bg-transparent hover:!bg-transparent focus:!ring-0 [&_img]:size-5 [&_img]:object-cover [&_img]:rounded-full" />
            <div className="h-4 w-px bg-border" />
            <ThemeSwitch />
          </div>
        </div>
      </div>
    </footer>
  );
};
